import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Form, Grid } from 'antd';

import HttpStatus from '../../../utility/status.enum.js';
import Notification from '../../../utility/notification.js';
import Functions from '../../../utility/functions.js';
import { useCMS } from '../../../utility/useCMS.js';
import { uploadFile } from '../../../redux/uploadFile/actionCreator.js';
import { getListingContent } from '../../../redux/manageListing/actionCreator.js';
import {
  getHostListingConfiguration,
  addEditHostListingConfiguration,
} from '../../../redux/manageListingConfigurration/actionCreator.js';
import { listingThemeNameList } from '../../../redux/manageTheme/actionCreator.js';
import { getConnectedUserForms } from '../../../redux/userForms/actionCreator.js';
import { fetchCmsContent } from '../../../redux/manageCms/actionCreator.js';

export const useHostListingConfig = () => {
  // ─── Constants
  const maxCharCount = global.maxCharCount;
  const MAX_HOUSE_RULES = global.maxHouseRule;
  const MOBILE_BREAKPOINT = global.mobileBreakPoint;

  // ─── Static shapes
  const listingMapJson = { slug: '', title: '', description: '', redirect_url: '', image: '', full_image_path: '' };
  const HouseRuleMapJson = { id: '', label: '' };

  // ─── Breakpoints & refs
  const screens = Grid.useBreakpoint();
  const waiverPlanRowRef = useRef(null);
  const quillEditorRef = useRef(null);
  const editorHouseManualRef = useRef(null);
  const editorCheckoutInstructionRef = useRef(null);

  // ─── Params & layout helpers
  const { listingSlug } = useParams();
  const { hostSlug: paramHostSlug } = useParams();
  const navigate = useNavigate();
  const cardsBodyPadding = screens.xs ? '20px 16px 24px' : '38px 38px 38px';
  const initialCardBodyPadding = screens.xs ? '20px 16px 24px' : '14px 38px 38px';
  const viewCardBodyPadding = screens.xs ? '20px 16px 24px' : '38px 38px 38px';

  // ─── Pure helpers (stable, no state dependency at declaration time)
  const isBlank = (value) => `${value ?? ''}`.trim() === '';
  const hoursArray = Functions.generateHoursArray();

  const nextHourFunction = (t, mode = 'ceil') => {
    if (!t) return '';
    const [timePartRaw, apRaw] = t.trim().split(/\s+/);
    const ap = apRaw ? apRaw.toUpperCase() : null;
    const [hStr, mStr = '0', sStr = '0'] = timePartRaw.split(':');
    let h = Number(hStr);
    const m = Number(mStr);
    const s = Number(sStr);
    if (Number.isNaN(h) || Number.isNaN(m) || Number.isNaN(s)) return '';
    if (ap === 'AM' || ap === 'PM') h = (h % 12) + (ap === 'PM' ? 12 : 0);
    if (mode === 'ceil' && (m > 0 || s > 0)) h = (h + 1) % 24;
    const displayHour = (h % 12 || 12).toString().padStart(2, '0');
    return `${displayHour}:00 ${h >= 12 ? 'PM' : 'AM'}`;
  };

  const daysBeforerules = [{ pattern: /^(0|[1-9][0-9]*)$/, message: 'Please enter only digits' }];

  // ─── Redux
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const { fetchCMSCommonFunction } = useCMS();

  // ─── Core state
  const [spinnerLoader, setSpinnerLoader] = useState(false);
  const [splitPanelHeight, setSplitPanelHeight] = useState(null);
  const [isMobileView, setIsMobileView] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= MOBILE_BREAKPOINT : false,
  );

  // ─── Image state
  const [previewImage, setPreviewImage] = useState('');
  const [showImageValidation, setImageValidation] = useState(false);
  const [listing_image, setListingImage] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  // ─── Checkin / checkout arrays
  const [unlockCheckInContentArray, setUnlockCheckInContentArray] = useState([]);
  const [unlockCheckInDoorCodeArray, setUnlockCheckInDoorCodeArray] = useState([]);
  const [lockCheckOutContentArray, setLockCheckOutContentArray] = useState([]);
  const [lockCheckOutDoorCodeArray, setLockCheckOutDoorCodeArray] = useState([]);
  const [displayArrivalCheckoutStatus, setDisplayArrivalCheckoutStatus] = useState(false);
  const [maxDoorDays, setMaxDoorDays] = useState(31);
  const [doorcodeStatus, setDoorcodeStatus] = useState(false);
  const [staticHelptext, setStaticHelptext] = useState()

  // ─── House rules
  const [house_rules, setHouseRules] = useState([]);
  const [isRequiredHouseRule, setHouseRulesRequired] = useState(false);
  const [maxLengthHouseRules, setMaxLengthHouseRules] = useState([]);

  // ─── Rich text content
  const [arrival_instructions, setArrivalInstructions] = useState('');
  const [rental_instructions, setRentalInstructions] = useState('');
  const [previewArrivalInstruction, setPreviewArrivalInstruction] = useState('');
  const [previewRentalInstruction, setPreviewRentalInstruction] = useState('');
  const [previewHouseManual, setPreviewHousemanual] = useState('');
  const [previewCheckoutInstructions, setPreviewCheckoutInstructions] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);

  // ─── Locals map
  const [addListingMap, setAddListingMap] = useState([]);
  const [isMapDescriptionRequired, setMapDescriptionRequired] = useState(false);
  const [isMapURLRequired, setMapURLRequired] = useState(false);
  const [isMapImageRequired, setMapImageRequired] = useState(false);
  const [isMapTitleRequired, setMapTitleRequired] = useState(false);

  // ─── Misc state
  const [hostSlug, setHostSlug] = useState(null);
  const [previewCmsContent, setPreviewCmsContent] = useState();

  // ─── Form / mode state
  const [useDefaultCheckTime, setUseDefaultCheckTime] = useState(false);
  const [byDefault, setByDefault] = useState(false);
  const [loginUser, setLoginUser] = useState([]);
  const [configuration, setConfiguration] = useState();
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState({});
  const [useIndividualRecommendation, setUseIndividualRecommendation] = useState(true);
  const [useGlobalHouseManual, setUseGlobalHouseManual] = useState(false);
  const [useGlobalCheckoutInstructions, setUseGlobalCheckoutInstructions] = useState(false);
  const prevUserHouseManualRef = useRef(useGlobalHouseManual);
  const prevUserCheckoutInstructionRef = useRef(useGlobalCheckoutInstructions);


  // ─── Theme state
  const [listingThemeDropdownList, setlistingThemeDropdownList] = useState([]);
  const [selectedThemeSlug, setSelectedThemeSlug] = useState(null);
  const [fetchSelectedThemeName, setSelectedThemeName] = useState('theme1');
  const [welcomVideoModal, setWelcomVideoModal] = useState('');
  const [themeInfoModal, setThemeInfoModal] = useState('');
  const [jotformActivePopup, setJotformActivePopup] = useState('');

  // ─── Secondary form state
  const [selectedForm, setSelectedForm] = useState(null);
  const [userFormsDropList, setuserFormsDropList] = useState([]);
  const [listingCheckingSlug, setListingCheckingSlug] = useState('');
  const [openthemeCms, setOpenthemeCms] = useState(false);
  const [earlyCheckInActivePopup, setEarlyCheckInActivePopup] = useState(0);
  const [state, setState] = useState({
    selectedRowKeys: 0,
    selectedRows: 0,
    visible: false,
    editVisible: false,
    modalType: 'primary',
    url: null,
    update: {},
  });

  // ─── Computed
  const hasConfiguration = configuration && Object.keys(configuration).length > 0;
  const selectedTheme = listingThemeDropdownList?.find((item) => item.slug === configuration?.theme_slug);

  // ─── Config normaliser
  const normalizeConfig = (result) => {
    if (!result) return {};
    let configData = Array.isArray(result) && result.length > 0 ? result[0] : result;
    if (typeof configData === 'string') {
      try {
        configData = JSON.parse(configData);
      } catch (e) {
        console.warn('Invalid JSON in config', e);
        configData = {};
      }
    }
    return configData;
  };

  // ─── Populate antd Form + all local state from a config object
  const populateForm = (configData) => {
    form.setFieldsValue({
      action_type: configData?.action_type,
      arrival_instructions: configData?.arrival_instructions,
      checkin_time: configData?.checkin_time,
      checkout_time: configData?.checkout_time,
      checkout_instructions: configData?.checkout_instructions,
      host_slug: configData?.host_slug,
      house_manual: configData?.house_manual,
      is_display_arrival_and_checkout_instruction: configData?.is_display_arrival_and_checkout_instruction,
      is_doorcode_visible: configData?.is_doorcode_visible,
      is_early_checkin_enable: configData?.is_early_checkin_enable,
      early_checkin_upsell_price: configData?.early_checkin_upsell_price,
      listing_image: configData?.listing_image,
      lock_content_after_checkout: configData?.lock_content_after_checkout,
      lock_door_code_after_checkout: configData?.lock_door_code_after_checkout,
      rental_instructions: configData?.rental_instructions,
      unlock_content_before_checkin: configData?.unlock_content_before_checkin,
      unlock_content_before_checkin_days: configData?.unlock_content_before_checkin_days,
      unlock_door_code_before_checkin: configData?.unlock_door_code_before_checkin,
      unlock_door_code_before_checkin_days: configData?.unlock_door_code_before_checkin_days,
      wifi_name: configData?.wifi_name,
      wifi_password: configData?.wifi_password,
      video: configData?.video,
      early_checkin_toggle: configData?.early_checkin_toggle,
      early_checkin_price: configData?.early_checkin_price,
      waiver_settings: configData?.waiver_settings,
      use_default_check_time: configData?.use_default_check_time,
      is_set_global_checkout_instructions: configData?.is_set_global_checkout_instructions,
      is_set_global_hose_manual: configData?.is_set_global_hose_manual,
    });

    setUseDefaultCheckTime(configData.use_default_check_time);
    setUseGlobalCheckoutInstructions(configData.is_set_global_checkout_instructions);
    setUseGlobalHouseManual(configData.is_set_global_hose_manual);
    setPreviewImage(configData?.listing_image_url);
    setListingImage(configData?.listing_image);
    setRentalInstructions(configData.rental_instructions);
    setPreviewRentalInstruction(configData.rental_instructions);
    setArrivalInstructions(configData.arrival_instructions);
    setPreviewArrivalInstruction(configData.arrival_instructions);
    setDoorcodeStatus(configData.is_doorcode_visible);
    setHouseRules(
      Array.isArray(configData.house_rules) && configData.house_rules.length
        ? configData.house_rules
        : [],
    );
    if (Array.isArray(configData.locals_map) && configData.locals_map.length > 0) {
      setAddListingMap(configData.locals_map);
    } else if (configData.locals_map?.length === 0) {
      setAddListingMap([]);
    }
    setSelectedForm(configData?.form_slug ?? null);
    setListingCheckingSlug(configData?.form_slug);
    setConfiguration(configData);
    setUseIndividualRecommendation(configData?.use_individual_recommendation === 1);
    setSelectedThemeSlug(configData?.theme_slug)
  };

  // ─── User / host slug init
  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    if (userData) {
      setLoginUser(userData);
      if (userData.role_id === 1) {
        if (paramHostSlug) setHostSlug(paramHostSlug);
      } else {
        setHostSlug(userData.slug);
        setByDefault(true);
      }
    }
    fetchThemeListing();
  }, [paramHostSlug]);

  // ─── Fetch host config on hostSlug change
  const fetchHostConfig = async () => {
      if (!hostSlug) return;
      
      // if (configuration) {
      //   populateForm(configuration);
      //   return;
      // }
      setSpinnerLoader(true);
      try {
        const response = await dispatch(getHostListingConfiguration(hostSlug, 1));
        if (response?.status === HttpStatus.HTTP_OK && response.result) {
          const configData = normalizeConfig(response.result || {});
          setConfiguration(configData);
          populateForm(configData);
        }
      } catch (error) {
        console.error('Error fetching host config:', error);
        form.resetFields();
        setHouseRules(house_rules);
      } finally {
        setSpinnerLoader(false);
      }
    };


  const handleCMSContent = async () => {
    try {
      const res = await fetchCMSCommonFunction(37);
      setStaticHelptext(res)
    }
    catch (error) {
      console.error('Failed to fetch CMS content:', error);
    }
  }

  useEffect(() => {
    fetchHostConfig();
    handleCMSContent();
  }, [hostSlug, dispatch, loginUser]);

  // ─── CMS content for house rules guideline modal
    const handleOpenModal = async  (id) => {
      try {
        setSpinnerLoader(true);
        setOpenthemeCms(true);
        const res = await fetchCMSCommonFunction(id);
        setPreviewCmsContent(res);
        // Open modal after content is ready
      } catch (error) {
        console.error('Failed to fetch CMS content:', error);
      } finally {
        // Always stop loading (success or error)
        setSpinnerLoader(false);
      }
    };

  // ─── Fetch listing content when entering edit mode
  useEffect(() => {
    if (!isEditMode) return;
    const fetchDataOnEditModeChange = async () => {
      try {
        const response = await dispatch(getHostListingConfiguration(hostSlug, 2));
        if (response?.status === HttpStatus.HTTP_OK && response?.result) {
          applyListingContent(response.result);
          setListingCheckingSlug(response.result?.form_slug);
        } else {
          const defaultContent = await dispatch(getListingContent());
          applyListingContent(defaultContent);
        }
      } catch (error) {
        try {
          const defaultContent = await dispatch(getListingContent());
          applyListingContent(defaultContent);
        } catch (err) {
          console.error(err);
        }
      }
    };
    fetchDataOnEditModeChange();
    getUserConnectedForms();
  }, [isEditMode, hostSlug, dispatch]);

  // ─── Sync global toggle state whenever configuration changes
  useEffect(() => {
    if (configuration) {
      const hasHouseManual = !!configuration?.house_manual;
      const hasCheckout = !!configuration?.checkout_instructions;
      setUseGlobalHouseManual(!hasHouseManual);
      setUseGlobalCheckoutInstructions(!hasCheckout);
      form.setFieldsValue({
        house_manual: configuration?.house_manual || '',
        checkout_instructions: configuration?.checkout_instructions || '',
      });
      setPreviewHousemanual(configuration?.house_manual || '');
      setPreviewCheckoutInstructions(configuration?.checkout_instructions || '');
    }
  }, [configuration]);

  // ─── Default door code values when doorcode is disabled
  useEffect(() => {
    if (!doorcodeStatus) {
      const checkinTime = form.getFieldValue('checkin_time');
      const checkoutTime = form.getFieldValue('checkout_time');
      if (!form.getFieldValue('unlock_door_code_before_checkin')) {
        form.setFieldsValue({ unlock_door_code_before_checkin: checkinTime });
      }
      if (!form.getFieldValue('lock_door_code_after_checkout')) {
        form.setFieldsValue({ lock_door_code_after_checkout: checkoutTime });
      }
    }
  }, [doorcodeStatus, form]);

  // ─── Theme slug sync
  useEffect(() => {
    if (selectedThemeSlug && hostSlug) {
      form.setFieldsValue({ theme_slug: selectedThemeSlug });
    }
    const selectThemeName = listingThemeDropdownList?.find(
      (theme) => theme?.slug === selectedThemeSlug,
    )?.applicable_css;
    setSelectedThemeName(selectThemeName ?? null);
  }, [selectedThemeSlug, hostSlug, listingThemeDropdownList, form]);

  useEffect(() => {
    if (selectedThemeSlug === null && listingThemeDropdownList?.length > 0) {
      setSelectedThemeSlug(listingThemeDropdownList[0].slug);
      form.setFieldsValue({ theme_slug: listingThemeDropdownList[0]?.slug });
    }
  }, [listingThemeDropdownList, selectedThemeSlug]);
  
  // ─── Image upload
  const handleImageChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    try {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
      if (!allowedTypes.includes(selectedFile.type)) {
        Notification.addNotificationError(
          'Unsupported image type. Please upload an image in JPG, PNG, GIF, BMP, or WebP format.',
        );
        return;
      }
      const compressedFile = await Functions.compressImage(selectedFile);
      setSpinnerLoader(true);
      const uploadData = new FormData();
      uploadData.append('imageType', 3);
      uploadData.append('is_default', 1);
      uploadData.append('file', compressedFile);
      const uploadFileResponse = await dispatch(uploadFile(uploadData));
      if (uploadFileResponse?.status === HttpStatus.HTTP_OK) {
        const { fileName, filePath } = uploadFileResponse.result;
        setPreviewImage(filePath);
        setListingImage(fileName);
        setSpinnerLoader(false);
        setImageValidation(false);
        setIsDirty(true);
      }
    } catch (error) {}
  };

  const deletePreviewImage = () => {
    setPreviewImage('');
    setListingImage(null);
    setImageValidation(true);
    setIsDirty(false);
  };

  // ─── Check-in / check-out time handlers
  const handleSelectCheckInTime = (value) => {
    const index = hoursArray.indexOf(value);
    if (index !== -1) {
      const newArray = hoursArray.slice(0, index + 1);
      setUnlockCheckInContentArray([...newArray]);
      setUnlockCheckInDoorCodeArray([...newArray]);
    }
    form.setFieldsValue({ unlock_content_before_checkin: value, unlock_door_code_before_checkin: value });
  };

  const handleSelectCheckOutTime = (value) => {
    const index = hoursArray.indexOf(value);
    const newArray = hoursArray.slice(index);
    setLockCheckOutContentArray(newArray);
    setLockCheckOutDoorCodeArray(newArray);
    form.setFieldsValue({ lock_content_after_checkout: value, lock_door_code_after_checkout: value });
  };

  const changeDisplayArrivalCheckoutInstructions = (status) => {
    const isChecked = typeof status === 'boolean' ? status : status?.target?.checked;
    setDisplayArrivalCheckoutStatus(!!isChecked);
    const checkinTime = form.getFieldValue('checkin_time');
    const checkoutTime = form.getFieldValue('checkout_time');
    const checkInIndex = hoursArray.indexOf(nextHourFunction(checkinTime, 'floor'));
    setUnlockCheckInDoorCodeArray(hoursArray.slice(0, checkInIndex + 1));
    setUnlockCheckInContentArray(hoursArray.slice(0, checkInIndex + 1))
    const checkOutIndex = hoursArray.indexOf(nextHourFunction(checkoutTime));
    setLockCheckOutDoorCodeArray(hoursArray.slice(checkOutIndex));
    setLockCheckOutContentArray(hoursArray.slice(checkOutIndex))
    form.setFieldsValue({ unlock_door_code_before_checkin: checkinTime, lock_door_code_after_checkout: checkoutTime });
    form.setFieldsValue({ unlock_content_before_checkin: checkinTime, lock_content_after_checkout: checkoutTime });
  };

  const handleInputChange = () => setIsDirty(true);

  const handleLinkClick = (event) => {
    event.stopPropagation();
    event.preventDefault();
  };

  const handleContentDaysChange = (value) => {
    const contentValue = value ?? 0;
    setMaxDoorDays(contentValue);
    const doorValue = form.getFieldValue('unlock_door_code_before_checkin_days');
    if (doorValue > contentValue) form.setFieldsValue({ unlock_door_code_before_checkin_days: contentValue });
  };

  const handleSelectContentCheckInTime = (value) => {
    const contentIndex = hoursArray.indexOf(value);
    const checkinValue = form.getFieldValue('checkin_time');
    const checkinIndex = hoursArray.indexOf(checkinValue);
    setUnlockCheckInDoorCodeArray(hoursArray.slice(contentIndex, checkinIndex + 1));
    form.setFieldsValue({ unlock_door_code_before_checkin: value });
  };

  const handleSelectContentCheckOutTime = (value) => {
    const contentIndex = hoursArray.indexOf(value);
    const checkoutIndex = hoursArray.indexOf(form.getFieldValue('checkout_time'));
    if (contentIndex !== -1 && checkoutIndex !== -1) {
      setLockCheckOutDoorCodeArray(hoursArray.slice(checkoutIndex, contentIndex + 1));
    }
    form.setFieldsValue({ lock_door_code_after_checkout: value });
  };

  const handleDoorCodeStatus = (status) => {
    const isChecked = typeof status === 'boolean' ? status : status?.target?.checked;
    setDoorcodeStatus(!!isChecked);
    if (isChecked) {
      form.resetFields([
        'unlock_door_code_before_checkin',
        'lock_door_code_after_checkout',
        'unlock_door_code_before_checkin_days',
      ]);
    }
  };

  // ─── House rules handlers
  const addHouseRule = () => {
    if (house_rules.length >= MAX_HOUSE_RULES) {
      Notification.addNotificationError(`You can add a maximum of ${MAX_HOUSE_RULES} house rules only.`);
      return;
    }
    if (house_rules.some((rule) => isBlank(rule.label))) {
      Notification.addNotificationError('Please fill house rule first');
      return;
    }
    const updatedRules = [...house_rules, { id: '', label: '' }].map((rule, index) => ({
      ...rule,
      id: `rule${index + 1}`,
    }));
    setHouseRules(updatedRules);
  };

  const deleteHouseRule = (i) => {
    const updatedRules = house_rules
      .filter((_, index) => index !== i)
      .map((rule, index) => ({ ...rule, id: `rule${index + 1}` }));
    setHouseRules(updatedRules);
  };

  const houseRulesChange = (e, i) => {
    const { value } = e.target;
    const updatedRules = house_rules.map((rule, index) => ({
      ...rule,
      id: `rule${index + 1}`,
      label: index === i ? value : rule.label,
    }));
    if (value.length > maxCharCount) {
      if (!maxLengthHouseRules.includes(i)) setMaxLengthHouseRules([...maxLengthHouseRules, i]);
    } else {
      setMaxLengthHouseRules(maxLengthHouseRules.filter((item) => item !== i));
    }
    setHouseRules(updatedRules);
    setIsDirty(true);
    if (house_rules.length > 0) setHouseRulesRequired(false);
  };

  // ─── Locals map handlers
  const handleLocalsImageChanges = async (e, index) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;
    try {
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/bmp', 'image/webp'];
      if (!allowedTypes.includes(selectedFile.type)) {
        Notification.addNotificationError(
          'Unsupported image type. Please upload an image in JPG, PNG, GIF, BMP, or WebP format.',
        );
        return;
      }
      const compressedFile = await Functions.compressImage(selectedFile);
      setSpinnerLoader(true);
      const uploadData = new FormData();
      uploadData.append('imageType', 2);
      uploadData.append('is_default', 1);
      uploadData.append('file', compressedFile);
      const uploadFileResponse = await dispatch(uploadFile(uploadData));
      if (uploadFileResponse?.status === HttpStatus.HTTP_OK) {
        const { fileName, filePath } = uploadFileResponse.result;
        const updatedListingMaps = [...addListingMap];
        updatedListingMaps[index].image = fileName;
        const reader = new FileReader();
        reader.onload = () => {
          updatedListingMaps[index].full_image_path = filePath;
          setAddListingMap([...updatedListingMaps]);
        };
        reader.readAsDataURL(compressedFile);
        setSpinnerLoader(false);
      }
    } catch (error) {}
  };

  const MAP_FIELD_KEYS = { 1: 'title', 2: 'description', 3: 'redirect_url' };

  const listingMapChange = (e, i, key) => {
    const { value } = e.target;
    const field = MAP_FIELD_KEYS[key];
    if (!field) return;
    const updatedListingMap = [...addListingMap];
    updatedListingMap[i][field] = value;
    setAddListingMap(updatedListingMap);
    setIsDirty(true);
  };

  const handleAddMapRow = () => {
    const validations = [
      { field: 'title', message: 'Please fill title first' },
      { field: 'description', message: 'Please fill description first' },
      { field: 'redirect_url', message: 'Please fill redirect_url first' },
      { field: 'image', message: 'Please upload image first' },
    ];
    for (const { field, message } of validations) {
      if (addListingMap.some((item) => isBlank(item[field]))) {
        Notification.addNotificationError(message);
        return;
      }
    }
    setAddListingMap([...addListingMap, listingMapJson]);
  };

  const handleDeleteMapRow = (i) => {
    setAddListingMap(addListingMap.filter((_, index) => index !== i));
  };

  // ─── Rich-text editor handlers
  const handleArrivalInstructionsValue = (data) => {
    setPreviewArrivalInstruction(data);
    form.setFieldsValue({ arrival_instructions: data });
  };

  const handlehouseManualValue = (data) => {
    setPreviewHousemanual(data);
    form.setFieldsValue({ house_manual: data });
  };

  const handleRentalInstructionsValue = (data) => {
    setPreviewRentalInstruction(data);
    form.setFieldsValue({ rental_instructions: data });
  };

  const handleCheckoutInstructionsValue = (data) => {
    setPreviewCheckoutInstructions(data);
    form.setFieldsValue({ checkout_instructions: data });
  };

  const insertVariable = (variable) => {
    if (quillEditorRef.current) quillEditorRef.current.insertTextAtCursor(variable);
    setIsModalVisible(false);
  };

  // ─── Apply default listing content (used when entering edit mode for a new listing)
  const applyListingContent = (data) => {
    if (listingSlug) return;
    const static_house_rules = data.house_rules;
    const static_locals_map = data.locals_map;
    const static_arrival_instructions = data.arrival_instructions;
    const static_rental_instructions = data.rental_instructions;
    let parsedHouseRules = [];
    try {
      parsedHouseRules = static_house_rules
        ? Array.isArray(static_house_rules)
          ? static_house_rules
          : JSON.parse(static_house_rules)
        : [];
    } catch (e) {
      parsedHouseRules = [];
    }
    if (parsedHouseRules.length > 0) setHouseRules(parsedHouseRules);
    if (Array.isArray(static_locals_map) && static_locals_map.length > 0) {
      setAddListingMap(static_locals_map);
    } else {
      setAddListingMap([listingMapJson])
    }
    setArrivalInstructions(static_arrival_instructions);
    setPreviewArrivalInstruction(static_arrival_instructions);
    setRentalInstructions(static_rental_instructions);
    setPreviewRentalInstruction(static_rental_instructions);
    const checkInIndex = hoursArray.indexOf(nextHourFunction(data?.checkin_time, 'floor'));
    const checkInNewArray = hoursArray.slice(0, checkInIndex + 1); // include the check-in time
    setUnlockCheckInContentArray(checkInNewArray);

    const isDisplayArrivalAndCheckoutInstruction = data?.is_display_arrival_and_checkout_instruction !== 0;
    const checkOutIndex = hoursArray.indexOf(nextHourFunction(data?.checkout_time));
    const checkOutNewArray = hoursArray.slice(checkOutIndex);
    
    setLockCheckOutContentArray(checkOutNewArray);
    const isDoorCodeVisible  = data?.is_doorcode_visible !== 0;

    const fieldsValue = {};
    if (data?.unlock_content_before_checkin) {
      fieldsValue.unlock_content_before_checkin = data?.unlock_content_before_checkin;
    }
    if (data?.lock_content_after_checkout) {
      fieldsValue.lock_content_after_checkout = data?.lock_content_after_checkout;
    }
    if (data?.unlock_content_before_checkin_days) { // Set the number of days before showing check-in content
      fieldsValue.unlock_content_before_checkin_days = data?.unlock_content_before_checkin_days;
    }
    setDoorcodeStatus(isDoorCodeVisible);
    setDisplayArrivalCheckoutStatus(isDisplayArrivalAndCheckoutInstruction);
    if (data?.unlock_door_code_before_checkin) {
      fieldsValue.unlock_door_code_before_checkin = data?.unlock_door_code_before_checkin;
    }
    if (data?.unlock_door_code_before_checkin_days) { // Set the number of days before showing check-in content
      fieldsValue.unlock_door_code_before_checkin_days = data?.unlock_door_code_before_checkin_days;
    }
    if (data?.lock_door_code_after_checkout) {
      fieldsValue.lock_door_code_after_checkout = data?.lock_door_code_after_checkout;
    }
    // If checkbox is checked: Door Code timing follows Check-in / Check-out time values to make dropdown.
    if(isDisplayArrivalAndCheckoutInstruction) {
      setUnlockCheckInDoorCodeArray(checkInNewArray);
      setLockCheckOutDoorCodeArray(checkOutNewArray);
    }else {
      const contentCheckInValue = nextHourFunction(data?.unlock_content_before_checkin, 'floor');
      const checkinValue = data?.checkin_time;
      const contentCheckInIndex = hoursArray.indexOf(contentCheckInValue);
      const checkinIndex = hoursArray.indexOf(checkinValue);
      if (contentCheckInIndex !== -1 && checkinIndex !== -1) {
        // Door code dropdown: content time → checkin time
        const contentCheckInNewArray = hoursArray.slice(contentCheckInIndex, checkinIndex + 1);
        setUnlockCheckInDoorCodeArray(contentCheckInNewArray);
      }
      const contentCheckOutValue = nextHourFunction(data?.lock_content_after_checkout);
      const checkoutValue = data?.checkout_time;
      const contentCheckOutIndex = hoursArray.indexOf(contentCheckOutValue);
      const checkoutIndex = hoursArray.indexOf(checkoutValue);
      if (contentCheckOutIndex !== -1 && checkoutIndex !== -1) {
        // Door code dropdown: checkout time → content lock time
        const contentCheckOutNewArray = hoursArray.slice(checkoutIndex, contentCheckOutIndex + 1);
        setLockCheckOutDoorCodeArray(contentCheckOutNewArray);
      }
    }
    setSelectedThemeSlug(data?.theme_slug)
    setUseIndividualRecommendation(data?.use_individual_recommendation ?? true);
    form.setFieldsValue(fieldsValue);
    form.setFieldsValue({ arrival_instructions: static_arrival_instructions });
    form.setFieldsValue({ rental_instructions: static_rental_instructions });
  };

  // ─── Submit
  const handleHostConfigSubmit = useCallback(
    async (values) => {
      try {
        const hostListingData = {
          listing_image: listing_image,
          theme_slug: values.theme_slug || '',
          video: values.video || '',
          wifi_name: values.wifi_name || '',
          wifi_password: values.wifi_password || '',
          use_default_check_time: useDefaultCheckTime ? 1 : 0,
          checkin_time: values.checkin_time || '',
          checkout_time: values.checkout_time || '',
          is_display_arrival_and_checkout_instruction: displayArrivalCheckoutStatus ? 1 : 0,
          unlock_content_before_checkin_days: values.unlock_content_before_checkin_days || 0,
          unlock_content_before_checkin: values.unlock_content_before_checkin || 0,
          lock_content_after_checkout: values.lock_content_after_checkout || 0,
          is_doorcode_visible: doorcodeStatus ? 1 : 0,
          unlock_door_code_before_checkin_days: values.unlock_door_code_before_checkin_days || 0,
          unlock_door_code_before_checkin: values.unlock_door_code_before_checkin || 0,
          lock_door_code_after_checkout: values.lock_door_code_after_checkout || 0,
          house_rules: house_rules?.length ? house_rules : [],
          use_individual_recommendation: useIndividualRecommendation ? 1 : 0,
          locals_map: useIndividualRecommendation ? [] : addListingMap,
          rental_instructions: values.rental_instructions ?? rental_instructions ?? '',
          arrival_instructions: values.arrival_instructions || '',
          house_manual: useGlobalHouseManual ? '' : values.house_manual || '',
          checkout_instructions: useGlobalCheckoutInstructions ? '' : values.checkout_instructions || '',
          form_slug: selectedForm || null,
          is_set_global_checkout_instructions: useGlobalCheckoutInstructions ? 1 : 0,
          is_set_global_hose_manual: useGlobalHouseManual ? 1 : 0,
        };

        setSpinnerLoader(true);
        const response = await dispatch(
          addEditHostListingConfiguration(
            { host_slug: hostSlug, user_listing_default_data: hostListingData },
            (res) => res,
          ),
        );
        if (response?.status === HttpStatus.HTTP_OK) {
          Notification.addNotificationSuccess(response.message);
          fetchHostConfig()

          const updatedData = { ...configuration, ...response.result };
          // setConfiguration(updatedData);
          // populateForm(updatedData);
          setIsEditMode(false);
          setIsDirty(false);
        } else {
          Notification.addNotificationError(response.message);
        }
      } catch (error) {
        console.error('Error submitting host config:', error);
        Notification.addNotificationError('Something went wrong!');
      } finally {
        setSpinnerLoader(false);
      }
    },
    [
      house_rules,
      listing_image,
      addListingMap,
      useDefaultCheckTime,
      displayArrivalCheckoutStatus,
      doorcodeStatus,
      useIndividualRecommendation,
      useGlobalHouseManual,
      useGlobalCheckoutInstructions,
      hostSlug,
      dispatch,
      selectedForm,
      previewImage,
    ],
  );

  // ─── Validation gate before submitting
  const checkValidation = () => {
    if (house_rules.some((rule) => isBlank(rule.label))) {
      setHouseRulesRequired(true);
      return;
    }
    form
      .validateFields()
      .then(() => form.submit())
      .catch(() => {});
  };

  // ─── Cancel edit — restore saved data
  const handleCancel = () => {
    setIsEditMode(false);
    form.setFieldsValue(configuration || {});
    setImageValidation(false);
    setHouseRulesRequired(false);
    setMaxLengthHouseRules([]);
    setMapURLRequired(false);
    setMapDescriptionRequired(false);
    setMapImageRequired(false);
    setHouseRules(configuration?.house_rules?.length ? configuration.house_rules : [HouseRuleMapJson]);
    setAddListingMap(configuration?.locals_map?.length ? configuration.locals_map : [listingMapJson]);
    setFormData(configuration || {});
  };
  // ─── Theme selection
  const fetchThemeListing = async () => {
    try {
      const listingThemeResponse = await dispatch(listingThemeNameList());
      if (listingThemeResponse.status === HttpStatus.HTTP_OK) {
        setlistingThemeDropdownList(listingThemeResponse.result);
      }
    } catch (error) {}
  };

  const handleSelectTheme = (themeSlugOrDirection) => {
    let themeSlug = themeSlugOrDirection;
    const currentSlug = form.getFieldValue('theme_slug');
    if (themeSlugOrDirection === 'prev' || themeSlugOrDirection === 'next') {
      const currentIndex = listingThemeDropdownList.findIndex((item) => item.slug === currentSlug);
      if (currentIndex === -1) {
        themeSlug = listingThemeDropdownList[0]?.slug;
      } else if (themeSlugOrDirection === 'prev') {
        const prevIndex = currentIndex === 0 ? listingThemeDropdownList.length - 1 : currentIndex - 1;
        themeSlug = listingThemeDropdownList[prevIndex].slug;
      } else {
        const nextIndex = currentIndex === listingThemeDropdownList.length - 1 ? 0 : currentIndex + 1;
        themeSlug = listingThemeDropdownList[nextIndex].slug;
      }
    }
    setSelectedThemeSlug(themeSlug);
    form.setFieldsValue({ theme_slug: themeSlug });
  };

  // ─── Secondary form
  const handleChangeFormsOptions = (e) => {
    setSelectedForm(e);
    setIsDirty(true);
  };

  const getUserConnectedForms = async () => {
    setSpinnerLoader(true);
    try {
      const connecteduserFormssData = await dispatch(getConnectedUserForms({ host_slug: hostSlug }));
      if (connecteduserFormssData?.status === HttpStatus.HTTP_OK) {
        setuserFormsDropList(connecteduserFormssData?.result);
      }
    } catch (error) {
    } finally {
      setSpinnerLoader(false);
    }
  };

  // ─── Global toggle helpers (house manual / checkout instructions)
  const handleToggleEditor = (checked, field, resetEditor) => {
    if (checked) {
      form.setFieldsValue({ [field]: '' });
      form.resetFields([field]);
      resetEditor?.();
    }
  };

  const handleGlobalCheckoutChange = (e) => {
    const checked = e.target.checked;
    setUseGlobalCheckoutInstructions(checked);
    handleToggleEditor(checked, 'checkout_instructions', () => setPreviewCheckoutInstructions(''));
  };

  const handleGlobalHouseManual = (e) => {
    const checked = e.target.checked;
    setUseGlobalHouseManual(checked);
    handleToggleEditor(checked, 'house_manual', () => setPreviewHousemanual(''));
  };

  const handleLocalMapCheckBox = (checked) => {
    setUseIndividualRecommendation(checked);
    if (!checked) {
      // UNCHECK → show inputs
      setAddListingMap((prev) =>
        prev && prev.length ? prev : [listingMapJson]
      );
    } 
    // else {
    //   // CHECK → hide inputs
    //   setAddListingMap([]);
    // }
  };

  const closeEarlyCheckInPopupInfo = () => {
    setState({
      ...state,
      earlyCheckinPopupInfo: false,
      visible: false,
      editVisible: false,
    });
  };

  const showEarlyCheckingInfo = (actionType) => {
    setEarlyCheckInActivePopup(actionType);
    setSpinnerLoader(true);
    const fetchCmsContentData = async () => {
      try {
        const fetchCmsContentResponse = await dispatch(
          fetchCmsContent({
            action_type: actionType,
          }),
        );
        if (fetchCmsContentResponse.status === HttpStatus.HTTP_OK) {
          const resultData = fetchCmsContentResponse?.result?.description;
          const hasIntercomArticleId = Functions.findIntercomArticleId(resultData); // Check if CMS content has an Intercom ID
          if (!hasIntercomArticleId) {
            // console.log(resultData, 'resultData');
            if (actionType === 27) {
              setThemeInfoModal(resultData?.listing_preview_theme_info);
            }
            if (actionType === 31) {
              setJotformActivePopup(resultData);
            }
            if (actionType === 32) {
              setWelcomVideoModal(resultData);
            }
            if (actionType === 33) {
              setWelcomVideoModal(resultData);
            }
            setState({
              ...state,
              earlyCheckinPopupInfo: true,
              visible: false,
              editVisible: false,
            });
          }
          setSpinnerLoader(false);
        } 
      } catch (error) { }
    };
    fetchCmsContentData();
  };

  useEffect(() => {
    // Only trigger when user UNCHECKS (true → false)
    if (prevUserHouseManualRef.current === true && useGlobalHouseManual === false) {
      setTimeout(() => {
        const editor = editorHouseManualRef.current?.querySelector(".ql-editor");
        if (editor) {
          editor.focus();
        }
      }, 0);
    }
    // update previous value
    prevUserHouseManualRef.current = useGlobalHouseManual;
  }, [useGlobalHouseManual]);

  useEffect(() => {
    // Only trigger when user UNCHECKS (true → false)
    if (prevUserCheckoutInstructionRef.current === true && useGlobalCheckoutInstructions === false) {
      setTimeout(() => {
        const editor = editorCheckoutInstructionRef.current?.querySelector(".ql-editor");
        if (editor) {
          editor.focus();
        }
      }, 0);
    }
    // update previous value
    prevUserCheckoutInstructionRef.current = useGlobalCheckoutInstructions;
  }, [useGlobalCheckoutInstructions]);

  return {
    // Form
    form,

    // Refs
    quillEditorRef,
    waiverPlanRowRef,

    // Layout helpers
    screens,
    cardsBodyPadding,
    initialCardBodyPadding,
    viewCardBodyPadding,
    splitPanelHeight,
    isMobileView,

    // Constants
    maxCharCount,
    MAX_HOUSE_RULES,
    hoursArray,
    daysBeforerules,
    isBlank,

    // Spinner
    spinnerLoader,

    // Image
    previewImage,
    showImageValidation,
    listing_image,
    isDirty,

    // Time arrays
    unlockCheckInContentArray,
    unlockCheckInDoorCodeArray,
    lockCheckOutContentArray,
    lockCheckOutDoorCodeArray,
    displayArrivalCheckoutStatus,
    maxDoorDays,
    doorcodeStatus,

    // House rules
    house_rules,
    isRequiredHouseRule,
    maxLengthHouseRules,

    // Rich text
    arrival_instructions,
    rental_instructions,
    previewArrivalInstruction,
    previewRentalInstruction,
    previewHouseManual,
    previewCheckoutInstructions,
    isModalVisible,

    // Locals map
    addListingMap,
    isMapDescriptionRequired,
    isMapURLRequired,
    isMapImageRequired,
    isMapTitleRequired,

    // Misc
    hostSlug,
    previewCmsContent,
    state,

    // Form / mode
    useDefaultCheckTime,
    byDefault,
    loginUser,
    configuration,
    isEditMode,
    formData,
    useIndividualRecommendation,
    useGlobalHouseManual,
    useGlobalCheckoutInstructions,

    // Theme
    listingThemeDropdownList,
    selectedThemeSlug,
    fetchSelectedThemeName,
    selectedTheme,

    // Secondary form
    selectedForm,
    userFormsDropList,
    listingCheckingSlug,
    openthemeCms,
    staticHelptext,
    // Computed
    hasConfiguration,
    welcomVideoModal,
    earlyCheckInActivePopup,
    themeInfoModal,
    jotformActivePopup,
    editorHouseManualRef,
    editorCheckoutInstructionRef,
    // Setters exposed to child components
    setIsEditMode,
    setIsModalVisible,
    setOpenthemeCms,
    setUseDefaultCheckTime,
    setUseIndividualRecommendation,
    setSelectedForm,
    setEarlyCheckInActivePopup,
    setThemeInfoModal,
    setWelcomVideoModal,
    setJotformActivePopup,

    // Handlers
    handleImageChange,
    deletePreviewImage,
    handleSelectCheckInTime,
    handleSelectCheckOutTime,
    changeDisplayArrivalCheckoutInstructions,
    handleInputChange,
    handleLinkClick,
    handleContentDaysChange,
    handleSelectContentCheckInTime,
    handleSelectContentCheckOutTime,
    handleDoorCodeStatus,
    addHouseRule,
    deleteHouseRule,
    houseRulesChange,
    handleLocalsImageChanges,
    listingMapChange,
    handleAddMapRow,
    handleDeleteMapRow,
    handleArrivalInstructionsValue,
    handlehouseManualValue,
    handleRentalInstructionsValue,
    handleCheckoutInstructionsValue,
    insertVariable,
    handleHostConfigSubmit,
    checkValidation,
    handleCancel,
    handleSelectTheme,
    handleChangeFormsOptions,
    handleGlobalCheckoutChange,
    handleGlobalHouseManual,
    handleOpenModal,
    handleLocalMapCheckBox,
    setStaticHelptext,
    showEarlyCheckingInfo,
    closeEarlyCheckInPopupInfo
  };
};
